import React, { useState, useEffect, useCallback } from 'react';
import { Users, RefreshCw, AlertCircle } from 'lucide-react';
import marathiService from '../../services/marathiService';


const MarathiSubscribers = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    const fetchSubscribers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = { 
                page: pagination.page,
                limit: pagination.limit
            };
            
            if (selectedType !== 'all') params.type = selectedType;
            
            // FIXED: Changed from marathiService.marathiBlogs.subscribers to marathiService.subscribers
            // Note: language parameter is automatically added by the service
            const response = await marathiService.subscribers.getAll(params);
            
            // Handle different response formats
            if (Array.isArray(response)) {
                setSubscribers(response);
                setPagination(prev => ({
                    ...prev,
                    total: response.length,
                    totalPages: Math.ceil(response.length / pagination.limit) || 1
                }));
            } else {
                // Ensure we have a proper response structure
                const data = response?.data || [];
                const total = response?.total || 0;
                const totalPages = response?.totalPages || 1;
                
                setSubscribers(data);
                setPagination(prev => ({
                    ...prev,
                    total,
                    totalPages
                }));
            }
        } catch (err) {
            console.error('Error fetching subscribers:', err);
            setError(err.message || 'Failed to fetch subscribers. Please try again later.');
            setSubscribers([]);
        } finally {
            setLoading(false);
        }
    }, [selectedType, pagination.page, pagination.limit]);

    useEffect(() => {
        fetchSubscribers();
    }, [fetchSubscribers]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({...prev, page: newPage}));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date)) return 'Invalid Date';
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch (e) {
            console.error('Date formatting error:', e);
            return 'Invalid Date';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-red-50">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-red-100 rounded-lg">
                <div className="flex flex-col items-center text-center">
                    <AlertCircle className="w-12 h-12 text-red-600 mb-4" />
                    <p className="text-red-800 mb-4">{error}</p>
                    <button
                        onClick={fetchSubscribers}
                        className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-red-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-black mb-2">Marathi Subscriber Management</h1>
                <button
                    onClick={fetchSubscribers}
                    disabled={loading}
                    className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-black mb-2">Filter by Type</label>
                    <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className="block w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    >
                        <option value="all">All</option>
                        <option value="content">Content</option>
                        <option value="newsletter">Newsletter</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-black mb-2">Items per page</label>
                    <select
                        value={pagination.limit}
                        onChange={(e) => setPagination({
                            ...pagination, 
                            limit: Number(e.target.value),
                            page: 1
                        })}
                        className="block w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                </div>
            </div>

            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-red-200">
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-black mb-4 flex items-center">
                        <Users className="w-6 h-6 mr-2 text-red-600" />
                        Subscribers ({pagination.total || 0})
                    </h2>
                    
                    {subscribers.length === 0 ? (
                        <p className="text-red-500 text-center py-4">No subscribers found.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-red-200">
                                    <thead className="bg-red-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">Subscribed On</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-red-100">
                                        {subscribers.map((subscriber) => (
                                            <tr key={subscriber._id || Math.random()} className="hover:bg-red-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {subscriber.email || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                                                    {subscriber.type || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {formatDate(subscriber.subscriptionDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${subscriber.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {subscriber.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center justify-between mt-4">
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                    className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-white"
                                >
                                    Previous
                                </button>
                                <span className="text-red-700">Page {pagination.page} of {pagination.totalPages}</span>
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                    className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-white"
                                >
                                    Next
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarathiSubscribers;